import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, Mail, Phone, Globe, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl bg-[#051221]/80 border border-[#00F2FE]/25 px-5 py-3.5 text-sm text-[#E8F1F5] placeholder:text-[#8AA1B1]/50 outline-none focus:border-[#00F2FE] focus:shadow-[0_0_20px_-6px_rgba(0,242,254,0.5)] transition-[border-color,box-shadow] duration-300";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received. We'll surface within 24 hours.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 lg:py-36 px-6" data-testid="contact-section">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <p className="text-xs tracking-[0.3em] uppercase text-[#00F2FE] mb-5">Chapter 06 — Start a project</p>
          <h2 className="font-['Outfit'] font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#E8F1F5] leading-tight">
            Ready to go <span className="gradient-text font-medium">deeper?</span>
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#8AA1B1] max-w-md">
            Tell us where your team is losing hours. We'll come back with a clear map of what
            AI can take off your plate — and what it's worth.
          </p>
          <div className="mt-10 space-y-5 text-sm">
            <a
              data-testid="contact-email-link"
              href="mailto:team@oceanmindai.co.uk"
              className="flex items-center gap-4 text-[#8AA1B1] hover:text-[#00F2FE] transition-colors duration-300 group"
            >
              <span className="w-10 h-10 rounded-full border border-[#00F2FE]/25 flex items-center justify-center bg-[#00F2FE]/5 group-hover:bg-[#00F2FE]/15 transition-colors duration-300">
                <Mail className="w-4 h-4 text-[#00F2FE]" />
              </span>
              team@oceanmindai.co.uk
            </a>
            <a
              data-testid="contact-phone-link"
              href="tel:+442037572456"
              className="flex items-center gap-4 text-[#8AA1B1] hover:text-[#00F2FE] transition-colors duration-300 group"
            >
              <span className="w-10 h-10 rounded-full border border-[#00F2FE]/25 flex items-center justify-center bg-[#00F2FE]/5 group-hover:bg-[#00F2FE]/15 transition-colors duration-300">
                <Phone className="w-4 h-4 text-[#00F2FE]" />
              </span>
              020 3757 2456
            </a>
            <a
              data-testid="contact-web-link"
              href="https://www.oceanmindai.co.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 text-[#8AA1B1] hover:text-[#00F2FE] transition-colors duration-300 group"
            >
              <span className="w-10 h-10 rounded-full border border-[#00F2FE]/25 flex items-center justify-center bg-[#00F2FE]/5 group-hover:bg-[#00F2FE]/15 transition-colors duration-300">
                <Globe className="w-4 h-4 text-[#00F2FE]" />
              </span>
              www.oceanmindai.co.uk
            </a>
            <div data-testid="contact-address" className="flex items-start gap-4 text-[#8AA1B1]">
              <span className="w-10 h-10 shrink-0 rounded-full border border-[#10E59A]/25 flex items-center justify-center bg-[#10E59A]/5">
                <MapPin className="w-4 h-4 text-[#10E59A]" />
              </span>
              <span className="leading-relaxed pt-2">
                4th Floor, 45 Silver Stream House,<br />
                Fitzroy Street, Fitzrovia,<br />
                London, W1T 6EB
              </span>
            </div>
          </div>
        </motion.div>

        <motion.form
          data-testid="contact-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="rounded-3xl glass border border-[#00F2FE]/20 p-8 lg:p-10 shadow-[0_0_60px_-20px_rgba(0,242,254,0.25)]"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <input
              data-testid="contact-name-input"
              required
              value={form.name}
              onChange={set("name")}
              placeholder="Your name"
              className={inputCls}
            />
            <input
              data-testid="contact-email-input"
              required
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="Work email"
              className={inputCls}
            />
          </div>
          <input
            data-testid="contact-company-input"
            value={form.company}
            onChange={set("company")}
            placeholder="Company (optional)"
            className={`${inputCls} mt-5`}
          />
          <textarea
            data-testid="contact-message-input"
            required
            rows={5}
            value={form.message}
            onChange={set("message")}
            placeholder="What would you like to automate?"
            className={`${inputCls} mt-5 resize-none`}
          />
          <button
            data-testid="contact-submit-button"
            type="submit"
            disabled={loading}
            className="mt-7 w-full rounded-full py-4 text-sm font-semibold bg-[#00F2FE] text-[#020813] hover:bg-[#10E59A] hover:shadow-[0_0_40px_-8px_rgba(16,229,154,0.7)] transition-[background-color,box-shadow] duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {loading ? "Sending..." : "Send Message"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
